import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Toast } from './Toast';

/**
 * GlobalToastStack — RC-224 app-shell toast queue manager.
 *
 * <p>The Toast primitive is presentation-only — it has no queue, no
 * positioning, no z-stack management. This component is the missing
 * "stack manager" referenced in Toast.jsx's header comment. Mount it
 * ONCE in DashboardLayoutV2 (the global app shell) and any consumer can
 * push a toast from anywhere via {@code pushGlobalToast({...})}.</p>
 *
 * <h3>Why a module-level push API instead of a React Context</h3>
 * Toast pushers are often imperative (event handlers, SSE callbacks,
 * api-response branches) — wrapping them in {@code useToast()} forces
 * every caller into a component and adds a hook dep to every effect
 * that wants to fire a toast. A function export is what callers
 * actually want; the React Context indirection adds friction without
 * value. Matches the SSE bus pattern in useFormTaskSseBus.js (singleton
 * outside the React tree).
 *
 * <h3>Stack semantics</h3>
 * - Toasts stack newest-on-top in the bottom-right corner of the viewport
 *   (z-index above slide-outs but below modals — the {@code z-50}
 *   placement matches the existing GlobalSearch overlay).
 * - Each toast auto-dismisses per Toast's own {@code duration} prop
 *   (default 4000ms). Manual dismiss + auto-dismiss both call the
 *   same {@code dismiss(id)} that removes from the queue.
 * - Max 5 visible at once — older toasts get bumped off the bottom
 *   when a 6th arrives (FIFO eviction). The contract isn't a perfect
 *   audit trail (those live in the audit log); it's a "what just
 *   happened" surface.
 *
 * <h3>Portal target</h3>
 * Mounted via {@link createPortal} to {@code document.body} so the toast
 * stack escapes any parent that uses {@code overflow: hidden} (canvas
 * pages do — without the portal, toasts would clip).
 *
 * <h3>SSR safety</h3>
 * Guards {@code document} access (currently the codebase is CRA-only and
 * SSR isn't a target, but the guard is one line and keeps the file safe
 * for any future Next.js / Remix migration).
 */

// ──────────────────────────────────────────────────────────────────────
// Singleton state — lives outside the React tree so any caller can push
// without importing context.
// ──────────────────────────────────────────────────────────────────────

let nextId = 1;
const listeners = new Set();
const queue = []; // [{ id, props }]

const MAX_VISIBLE = 5;

/**
 * Push a toast onto the global queue. Returns the new toast's id so the
 * caller can dismiss it imperatively (e.g. an action that completes
 * before the auto-dismiss).
 *
 * @param {Object} props — passed through to {@link Toast}: title,
 *   description, tone, duration, action. {@code onDismiss} is supplied
 *   by the stack and should not be set by callers (it would be
 *   overwritten anyway).
 * @returns {number} the new toast id
 */
export function pushGlobalToast(props) {
  const id = nextId++;
  const next = { id, props: { ...props } };
  queue.push(next);
  // Cap the visible queue. FIFO eviction keeps the most recent N.
  while (queue.length > MAX_VISIBLE) queue.shift();
  notify();
  return id;
}

/** Dismiss a specific toast by id. No-op if not in the queue. */
export function dismissGlobalToast(id) {
  const idx = queue.findIndex((t) => t.id === id);
  if (idx < 0) return;
  queue.splice(idx, 1);
  notify();
}

/** Clear all toasts. Useful on logout / scope-switch to drop stale state. */
export function clearGlobalToasts() {
  queue.length = 0;
  notify();
}

function notify() {
  // Snapshot so listeners see a stable array even mid-mutation.
  const snapshot = queue.slice();
  listeners.forEach((cb) => {
    try { cb(snapshot); } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[GlobalToastStack] listener threw', e);
    }
  });
}

function subscribe(cb) {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

// ──────────────────────────────────────────────────────────────────────
// React component — mounts once in the app shell, renders the queue.
// ──────────────────────────────────────────────────────────────────────

/**
 * Render the global toast stack. Mount once in DashboardLayoutV2; do
 * NOT mount multiple instances (you'd get N renders of the same queue,
 * but they'd all live on top of each other in the same portal target).
 */
export default function GlobalToastStack() {
  const [items, setItems] = useState(queue.slice());

  useEffect(() => subscribe(setItems), []);

  if (typeof document === 'undefined') return null;
  if (items.length === 0) return null;

  return createPortal(
    <div
      // Tailwind layout-only utilities — no inline style with hex/padding.
      // Bottom-right positioning matches the existing convention used by
      // /flags toast banner; z-50 sits above slide-outs (z-40) and below
      // modals (z-60).
      className={
        'fixed bottom-4 right-4 z-50 ' +
        'flex flex-col-reverse gap-2 pointer-events-none'
      }
      role="region"
      aria-label="Notifications"
    >
      {items.map((t) => (
        // pointer-events-auto so individual toasts ARE clickable / dismissible
        // even though the wrapper is pass-through (so canvas drags etc.
        // aren't blocked when the stack is empty).
        <div key={t.id} className="pointer-events-auto">
          <Toast
            {...t.props}
            onDismiss={() => {
              dismissGlobalToast(t.id);
              // Honor a caller's onDismiss too if they passed one for
              // their own analytics — fire it AFTER the stack removal so
              // the toast disappears immediately even if the callback
              // throws.
              const userOnDismiss = t.props?._userOnDismiss;
              if (typeof userOnDismiss === 'function') {
                try { userOnDismiss(); } catch (e) {
                  // eslint-disable-next-line no-console
                  console.error('[GlobalToastStack] user onDismiss threw', e);
                }
              }
            }}
          />
        </div>
      ))}
    </div>,
    document.body,
  );
}

// ──────────────────────────────────────────────────────────────────────
// Test-only escape hatches.
// ──────────────────────────────────────────────────────────────────────

export const __TEST__ = {
  reset() {
    queue.length = 0;
    listeners.clear();
    nextId = 1;
  },
  queueSnapshot() {
    return queue.slice();
  },
};
